import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { getApiUrl } from '../../api'

interface Attachment {
  id: number
  file_type: string
  file_url: string
  file_name: string | null
  sort_order: number | null
}

interface EventDetail {
  event_id: number
  author_id: number | null
  title: string
  content: string
  is_visible: number
  created_at: string
  updated_at: string
  attachments: Attachment[]
}

export default function EventDetail() {
  const { event_id } = useParams<{ event_id: string }>()
  const navigate = useNavigate()
  const [event, setEvent] = useState<EventDetail | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchEvent()
  }, [event_id])

  const fetchEvent = async () => {
    setLoading(true)
    try {
      const res = await fetch(getApiUrl(`/marubit/api/event/${event_id}`))
      if (!res.ok) throw new Error('조회 실패')
      const data = await res.json()
      setEvent(data)
    } catch {
      alert('행사 정보를 불러오는 데 실패했습니다.')
      navigate('/event')
    } finally {
      setLoading(false)
    }
  }

  const formatDate = (dateStr: string) =>
    new Date(dateStr).toLocaleDateString('ko-KR', {
      year: 'numeric', month: '2-digit', day: '2-digit',
    })

  const images = event?.attachments.filter(a => a.file_type === 'image') ?? []
  const files = event?.attachments.filter(a => a.file_type === 'file') ?? []

  if (loading) return (
    <div className="min-h-[60vh] flex items-center justify-center text-gray-300">
      불러오는 중...
    </div>
  )

  if (!event) return null

  return (
    <div className="bg-[#fafafa] min-h-screen px-6 py-12">
      <div className="max-w-[1200px] mx-auto">

        {/* 뒤로가기 */}
        <button
          onClick={() => navigate('/event')}
          className="mb-6 text-[14px] text-gray-400 hover:text-[#887267] transition-colors flex items-center gap-1"
        >
          ← 목록으로
        </button>

        {/* 제목 */}
        <div className="bg-white rounded-2xl p-7 mb-4 f8b62cshadow-[0_2px_12px_rgba(136,114,103,0.06)]">
          <h1 className="text-[22px] font-extrabold text-[#222] mb-3">{event.title}</h1>
          <div className="flex gap-4 text-[12px] text-gray-400">
            <span>등록일 {formatDate(event.created_at)}</span>
            <span>수정일 {formatDate(event.updated_at)}</span>
          </div>
        </div>

        {/* 본문 */}
        <div className="bg-white rounded-2xl p-7 mb-4 f8b62cshadow-[0_2px_12px_rgba(136,114,103,0.06)]">
          <h2 className="text-[16px] font-bold text-[#887267] border-l-[3px] border-[#887267] pl-[10px] mb-4">
            내용
          </h2>
          <style>{`
            .event-content h1 { font-size: 2em; font-weight: bold; margin: 0.5em 0; }
            .event-content h2 { font-size: 1.5em; font-weight: bold; margin: 0.5em 0; }
            .event-content ul { list-style-type: disc; padding-left: 1.5em; margin: 0.5em 0; }
            .event-content ol { list-style-type: decimal; padding-left: 1.5em; margin: 0.5em 0; }
            .event-content p { margin: 0.3em 0; line-height: 1.7; }
            .event-content img { max-width: 100%; height: auto; border-radius: 8px; }
            .event-content hr { border: none; border-top: 1px solid #eee; margin: 1em 0; }
            .event-content strong { font-weight: bold; }
            .event-content em { font-style: italic; }
          `}</style>
          <div
            className="event-content text-[14px] text-[#333] leading-relaxed"
            dangerouslySetInnerHTML={{ __html: event.content }}
          />
        </div>

        {/* 이미지 */}
        {images.length > 0 && (
        <div className="bg-white rounded-2xl p-7 mb-4 f8b62cshadow-[0_2px_12px_rgba(136,114,103,0.06)]">
            <h2 className="text-[16px] font-bold text-[#887267] border-l-[3px] border-[#887267] pl-[10px] mb-4">
            이미지
            </h2>
            <ul className="space-y-2">
            {images.map(img => (
                <li key={img.id}>
                <a
                    href={img.file_url}
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center gap-2 text-[13px] text-[#887267] hover:underline"
                >
                    <span>🖼</span>{img.file_name ?? '이미지 보기'}
                </a>
                </li>
            ))}
            </ul>
        </div>
        )}

        {/* 첨부파일 */}
        {files.length > 0 && (
          <div className="bg-white rounded-2xl p-7 mb-6 f8b62cshadow-[0_2px_12px_rgba(136,114,103,0.06)]">
            <h2 className="text-[16px] font-bold text-[#887267] border-l-[3px] border-[#887267] pl-[10px] mb-4">
              첨부파일
            </h2>
            <ul className="space-y-2">
                {files.map(file => (
                <li key={file.id}>
                    <a
                    href={file.file_url}
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center gap-2 text-[13px] text-[#887267] hover:underline"
                    >
                    <span>📎</span>{file.file_name ?? '파일 다운로드'}
                    </a>
                </li>
                ))}
            </ul>
          </div>
        )}

        {/* 수정 버튼 (관리자용) */}
        <div className="flex gap-3">
          {/* <button
            onClick={() => navigate(`/event/edit/${event.event_id}`)}
            className="flex-1 h-[52px] bg-[#887267] text-white border-none rounded-xl text-[16px] font-bold cursor-pointer hover:bg-[#6b5248] transition-colors"
          >
            수정
          </button>
          <button
            onClick={() => navigate('/event')}
            className="flex-1 h-[52px] bg-white text-[#887267] border-[1.5px] border-[#887267] rounded-xl text-[16px] font-bold cursor-pointer hover:bg-[#f5f2f0] transition-colors"
          >
            목록
          </button> */}
        </div>
      </div>
    </div>
  )
}